/**
 * Shared React Quill (toolbar + image upload) for question text in Question Bank and Mock Exam admin UIs.
 */
import React, { useRef, useMemo, forwardRef } from "react";
import { Box } from "@mui/material";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api from "../api";
import { toast } from "react-toastify";
import MathToolbar from "../components/MathToolbar";

export const QUILL_TOOLBAR_CONTAINER = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ script: "sub" }, { script: "super" }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ indent: "-1" }, { indent: "+1" }],
  ["link", "image"],
  ["clean"],
];

async function uploadFileToR2(file) {
  const formData = new FormData();
  formData.append("image", file);
  const res = await api.post("/admin/question-bank/upload-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const url = res.data?.data?.image_url;
  if (!url) throw new Error("No image URL returned");
  return url;
}

/** `this` is Quill toolbar; uploads to Cloudflare R2 via question-bank image endpoint. */
export function quillImageHandler() {
  const quill = this.quill;
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/png,image/jpeg,image/webp");
  input.click();
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const url = await uploadFileToR2(file);
      const range = quill.getSelection(true) || { index: quill.getLength() };
      quill.insertEmbed(range.index, "image", url, "user");
      quill.setSelection(range.index + 1);
    } catch {
      toast.error("Image upload failed");
    }
  };
}

export const QUILL_MODULES = {
  toolbar: {
    container: QUILL_TOOLBAR_CONTAINER,
    handlers: {
      image: quillImageHandler,
    },
  },
};

export const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
];

// Passage-specific toolbar: adds alignment and drops header/strike/indent
// to keep the toolbar compact for long-form reading passages.
export const PASSAGE_QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [false, 2, 3] }],
      ["bold", "italic", "underline"],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      ["image"],
      ["clean"],
    ],
    handlers: { image: quillImageHandler },
  },
};

export const PASSAGE_QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "align",
  "list",
  "bullet",
  "script",
  "image",
];

/**
 * Drop-in replacement for <ReactQuill> that prevents base64 image data from
 * being saved. When the user pastes or drops an image, Quill would normally
 * embed it as a data:image/... URL. This component detects those base64 srcs
 * in the onChange output, uploads them to Cloudflare R2, and replaces them
 * with the returned public URL before calling the parent onChange.
 */
export const QuillR2Editor = forwardRef(function QuillR2Editor({ value, onChange, modules, formats, style, showMathTools, ...rest }, ref) {
  const localRef = useRef(null);
  const uploadingRef = useRef(false);

  // Combined ref: keeps localRef in sync (for MathToolbar) and also satisfies the forwarded external ref.
  const combinedRef = (el) => {
    localRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  // Each editor needs its own toolbar module object: Quill mutates the toolbar module during init,
  // so sharing one `modules` reference across several ReactQuill instances leaves the custom image
  // handler bound to only one of them — on the others the toolbar image button does nothing. Rebuild
  // a per-instance modules object that always carries the R2 image handler.
  const instanceModules = useMemo(() => {
    const base = modules ?? QUILL_MODULES;
    const container = base?.toolbar?.container ?? base?.toolbar ?? QUILL_TOOLBAR_CONTAINER;
    return {
      ...base,
      toolbar: {
        container,
        handlers: { ...(base?.toolbar?.handlers ?? {}), image: quillImageHandler },
      },
    };
  }, [modules]);

  const handleChange = async (html) => {
    // Check for base64 images before calling parent onChange
    const base64Re = /src="(data:image\/[^"]+)"/g;
    if (!base64Re.test(html) || uploadingRef.current) {
      onChange(html);
      return;
    }

    uploadingRef.current = true;
    let processed = html;
    const matches = [...html.matchAll(/src="(data:image\/[^"]+)"/g)];

    for (const match of matches) {
      try {
        const dataUrl = match[1];
        const fetchRes = await fetch(dataUrl);
        const blob = await fetchRes.blob();
        const ext = blob.type.split("/")[1] || "png";
        const file = new File([blob], `pasted.${ext}`, { type: blob.type });
        const url = await uploadFileToR2(file);
        processed = processed.replace(match[0], `src="${url}"`);
      } catch {
        toast.error("Pasted image upload failed");
        // Leave base64 in place rather than losing the content entirely
      }
    }

    uploadingRef.current = false;
    onChange(processed);
  };

  return (
    <Box>
      {showMathTools && (
        <MathToolbar getEditor={() => localRef.current?.getEditor()} />
      )}
      <ReactQuill
        ref={combinedRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={instanceModules}
        formats={formats ?? QUILL_FORMATS}
        style={style}
        {...rest}
      />
    </Box>
  );
});

/** Plain text length for validation (strip HTML tags). */
export function plainTextLength(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, "")
    .trim().length;
}
